import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TableState } from "../../logic/tableStatus";

const BADGE_TONE: Record<TableState["kind"], string> = {
  free: "border-schema-fits-edge bg-surface text-schema-fits-ink",
  reserved: "border-schema-reserved-edge-soft bg-schema-reserved text-ink",
  occupied: "border-schema-occupied-edge bg-schema-occupied text-schema-occupied-ink",
};

const PILL_TONE: Record<TableState["kind"], string> = {
  free: "bg-ok-tint text-schema-fits-ink",
  reserved: "bg-brand-tint text-brand-deep-ink",
  occupied: "bg-danger-tint text-schema-occupied-ink",
};

// Без статус (маса извън схемата, закрита без бон) — неутралната значка.
const NEUTRAL_BADGE = "border-shade-3 bg-surface text-ink";

/** Статусът на масата като pill („Свободна" / „Резервирана" / „Заета"). */
const TableStatusPill = ({ status }: { status: TableState["kind"] }) => {
  const { t } = useTranslation();
  return (
    <span className={`shrink-0 rounded-full px-2.5 py-1 text-[12px] font-bold leading-[1.2] ${PILL_TONE[status]}`}>
      {t(`reservations.popover.status.${status}`)}
    </span>
  );
};

/**
 * Заглавието на една маса: номерът в значка в стила на статуса, „Маса 1",
 * подзаглавие („5 места · Основна зала") и статусът (`TableStatusPill`).
 * `children` — след статуса (× на поповъра).
 */
const TableHeading = ({
  name,
  title,
  subtitle,
  isRound,
  status,
  children,
}: {
  name: string;
  title: string;
  subtitle: ReactNode;
  isRound: boolean;
  // null — без статус и с неутрална значка.
  status: TableState["kind"] | null;
  children?: ReactNode;
}) => {
  return (
    <div className="flex items-center gap-2.5">
      <span
        // min-w вместо фиксирана ширина — дълго име („E14", „Бар 1") разширява значката.
        className={`flex h-8 min-w-8 shrink-0 items-center justify-center border-2 px-1 text-[14px] font-bold ${
          isRound ? "rounded-full" : "rounded-[8px]"
        } ${status ? BADGE_TONE[status] : NEUTRAL_BADGE}`}
      >
        {name}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-bold leading-[1.2] tracking-[-0.2px] text-ink">{title}</div>
        <div className="mt-0.5 text-[12px] font-medium leading-[1.3] text-muted">{subtitle}</div>
      </div>
      {status && <TableStatusPill status={status} />}
      {children}
    </div>
  );
};

export default TableHeading;
